import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CeilingForm    from "./CeilingForm";
import PoolStatusCard from "./PoolStatusCard";
import HistoryTable   from "./HistoryTable";
import { getCurrentQuarter } from "./constants";
import { setUblCeiling, getUblPoolStatus, getUblPoolSettings } from "../../../../../../../api/ublPool";

const UBLPoolModal = ({ onClose }) => {
  const theme  = useTheme();
  const t      = theme.custom;

  const currentQuarter = getCurrentQuarter();

  // null = no quarter selected (show all history)
  // currentQuarter = selected (filter history + load status)
  const [selectedQuarter, setSelectedQuarter] = useState(null);

  const [status,     setStatus]     = useState(null);
  const [history,    setHistory]    = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [apiError,   setApiError]   = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);

  // Fetch full history once on mount — always shown unfiltered by default
  useEffect(() => {
    getUblPoolSettings()
      .then(setHistory)
      .catch(() => {});
  }, []);

  // Fetch pool status whenever quarter is selected
  // Clear status when deselected
  useEffect(() => {
    if (!selectedQuarter) {
      setStatus(null);
      setApiError(null);
      return;
    }

    setLoading(true);
    setApiError(null);
    setStatus(null);

    getUblPoolStatus(selectedQuarter)
      .then(setStatus)
      .catch((err) => {
        if (err.response?.status === 404) {
          // No ceiling set for this quarter yet — not a real error
          setStatus(null);
        } else {
          setApiError("Failed to load pool status");
        }
      })
      .finally(() => setLoading(false));
  }, [selectedQuarter]);

  const handleSetCeiling = async (ceiling) => {
    // Ceiling can only be set when a quarter is selected
    if (!selectedQuarter) return;

    try {
      setSubmitting(true);
      setApiError(null);
      setSuccessMsg(null);

      const result = await setUblCeiling({ quarter: selectedQuarter, ceiling });
      setStatus(result);

      // Refresh history to include the new entry
      const updated = await getUblPoolSettings();
      setHistory(updated);

      setSuccessMsg(`Ceiling set to ${ceiling} for ${selectedQuarter}`);
    } catch (err) {
      const data = err.response?.data;
      setApiError(data?.error || data?.message || "Failed to set ceiling");
    } finally {
      setSubmitting(false);
    }
  };

  // Toggle — clicking selected quarter deselects it, clicking unselected selects it
  const handleQuarterToggle = () => {
    setSelectedQuarter((prev) => prev ? null : currentQuarter);
    setSuccessMsg(null);
    setApiError(null);
  };

  return (
    <>
      {/* Backdrop */}
      <Box
        onClick={onClose}
        sx={{
          position: "fixed", inset: 0,
          background: "rgba(0,0,0,0.35)", zIndex: 299,
        }}
      />

      {/* Modal panel */}
      <Box sx={{
        position: "fixed", top: "50%", left: "50%",
        transform: "translate(-50%, -50%)",
        width: "480px", maxHeight: "80vh",
        zIndex: 300,
        background: t.surface,
        border: `1px solid ${t.border2}`,
        borderRadius: "6px",
        boxShadow: "0 16px 48px rgba(0,0,0,0.35)",
        display: "flex", flexDirection: "column",
        overflow: "hidden",
      }}>

        {/* Header */}
        <Box sx={{
          display: "flex", alignItems: "flex-start",
          justifyContent: "space-between",
          padding: "18px 20px 14px",
          borderBottom: `1px solid ${t.border2}`,
          flexShrink: 0,
        }}>
          <Box>
            <Typography sx={{
              fontFamily: "'Inter', sans-serif",
              fontSize: "15px", fontWeight: 700,
              color: t.text, letterSpacing: "-0.01em",
            }}>
              UBL Pool Ceiling
            </Typography>
            <Typography sx={{
              fontFamily: "'DM Mono', monospace",
              fontSize: "9px", color: t.text3,
              letterSpacing: "0.08em", mt: "3px",
            }}>
              PMO · Finance-approved headcount
            </Typography>
          </Box>
          <Box
            onClick={onClose}
            sx={{
              width: "28px", height: "28px",
              display: "flex", alignItems: "center", justifyContent: "center",
              borderRadius: "3px", cursor: "pointer",
              color: t.text3, fontSize: "14px",
              transition: "all 0.15s",
              "&:hover": { background: t.surface2, color: t.text },
            }}
          >
            ✕
          </Box>
        </Box>

        {/* Scrollable body */}
        <Box sx={{ flex: 1, overflowY: "auto", padding: "16px 20px" }}>

          {/* Quarter toggle — only current quarter is available to select */}
          <Typography sx={{
            fontFamily: "'DM Mono', monospace",
            fontSize: "8px", letterSpacing: "0.15em",
            textTransform: "uppercase", color: t.text3, mb: "8px",
          }}>
            Quarter
          </Typography>

          <Box sx={{ display: "flex", gap: "6px", mb: "16px" }}>
            <Box
              onClick={handleQuarterToggle}
              sx={{
                display: "inline-flex", alignItems: "center",
                padding: "5px 12px", borderRadius: "3px",
                cursor: "pointer", transition: "all 0.15s",
                border: selectedQuarter
                  ? `1px solid ${t.accent}88`
                  : `1px solid ${t.border2}`,
                background: selectedQuarter ? t.accent2 : t.surface2,
                color: selectedQuarter ? t.accent : t.text2,
                "&:hover": {
                  borderColor: t.accent + "66",
                  color: t.accent,
                  background: t.accent2,
                },
              }}
            >
              <Typography sx={{
                fontFamily: "'DM Mono', monospace",
                fontSize: "11px", fontWeight: selectedQuarter ? 600 : 400,
              }}>
                {currentQuarter}
              </Typography>
              {/* Small indicator dot when selected */}
              {selectedQuarter && (
                <Box sx={{
                  width: "5px", height: "5px", borderRadius: "50%",
                  background: t.accent, ml: "7px", flexShrink: 0,
                }} />
              )}
            </Box>

            {/* Clear hint — only visible when selected */}
            {selectedQuarter && (
              <Typography
                onClick={handleQuarterToggle}
                sx={{
                  fontFamily: "'DM Mono', monospace",
                  fontSize: "9px", color: t.text3,
                  cursor: "pointer", alignSelf: "center",
                  "&:hover": { color: t.text2 },
                }}
              >
                click to deselect
              </Typography>
            )}
          </Box>

          {/* Ceiling form — only shown when a quarter is selected */}
          {selectedQuarter && (
            <>
              <CeilingForm
                currentCeiling={status?.ceiling ?? ""}
                onSubmit={handleSetCeiling}
                submitting={submitting}
              />

              {/* Success message */}
              {successMsg && (
                <Box sx={{
                  mt: "10px", padding: "8px 12px",
                  background: "rgba(40,179,180,0.08)",
                  border: "1px solid rgba(40,179,180,0.3)",
                  borderRadius: "3px",
                }}>
                  <Typography sx={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "11px", color: t.accent,
                  }}>
                    {successMsg}
                  </Typography>
                </Box>
              )}

              {/* API error */}
              {apiError && (
                <Box sx={{
                  mt: "10px", padding: "8px 12px",
                  background: "rgba(200,64,64,0.08)",
                  border: "1px solid rgba(200,64,64,0.3)",
                  borderRadius: "3px",
                }}>
                  <Typography sx={{
                    fontFamily: "'DM Mono', monospace",
                    fontSize: "11px", color: "#c84040",
                  }}>
                    {apiError}
                  </Typography>
                </Box>
              )}

              {/* Pool status breakdown — shown after fetch */}
              {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: "20px" }}>
                  <CircularProgress size={16} sx={{ color: t.accent }} />
                </Box>
              ) : (
                <PoolStatusCard status={status} />
              )}
            </>
          )}

          {/* History table
              No quarter selected → show all records unfiltered
              Quarter selected    → filter to that quarter only */}
          <HistoryTable
            settings={history}
            activeQuarter={selectedQuarter}
          />
        </Box>
      </Box>
    </>
  );
};

export default UBLPoolModal;